import React, { useEffect, useState } from 'react';
import './BuyersEngagementDocuments.scss';
import filterFactory, {
    FILTER_TYPES,
} from '../../../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import BootstrapTable from '../../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import moment from 'moment';
import { useStateSelector } from '../../../../../store/selectors';
import { useDispatch } from 'react-redux';
import {
    TableFilter,
    TimelineDocumentsTableFilter,
} from '../../../../../components/Table/models/table-filter';
import { EngagementDocumentStatus } from '../../../../../slices/marketplace/timeline/models/enums/engagement-document-status';
import { ActionDocumentType } from '../../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import { DocumentSection } from '../../../../../slices/marketplace/timeline/models/document-section.model';
import createNotification from '../../../../../utils/createNotification';
import { DocumentSignModal } from '../../../../../components/DocumentSignModal/DocumentSignModal';
import { EngagementBuyer } from '../../../../../slices/marketplace/timeline/models/engagement-buyer.model';
import BuyerProfilePreviewPopup from '../../../../../components/BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';
import CollapsibleComponent from '../../../../../components/Collapsible/CollapsibleComponent';
import { GetOpportunityDocuments } from '../../../../../slices/marketplace/opportunity/thunks/get-opportunity-documnts.thunk';
import { MarketplaceOpportunityDocument } from '../../../../../slices/marketplace/opportunity/models/opportunity-document';
import { TimelineParty } from '../../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineEventStatus } from '../../../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { PrefilledTags } from '../../../../../models/PrefilledTags/PrefilledTags';
import { GetOpportunity } from '../../../../../slices/marketplace/opportunity/thunks/get-opportunity.thunk';
import LegalDocumentInformationPopup from '../../../../../components/LegalDocumentInformationPopup/LegalDocumentInformationPopup';
import { DocumentDetailsContentType } from '../../../../../slices/marketplace/timeline/models/enums/document-details-content-type';
import { EngagementDocumentSource } from '../../../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import { LevelOfService } from '../../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { LinkedDataRequestProject } from '../../../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import ProjectItemViewPopup from '../../../../ConnectMarketplaceDashboard/ProjectItemViewPopup/ProjectItemViewPopup';
import { EngagementDocument } from '../../../../../slices/marketplace/timeline/models/engagement-document';
import EngagementDocumentAccessPaymentComponent from '../../../../../components/PaymentDialog/EngagementDocumentAccessPaymentComponent/EngagementDocumentAccessPaymentComponent';
import {
    EngagementPaymentPricingDetails,
    PricesCalculationHelper,
} from '../../../../../utils/pricesCalculationHelper';
import { DocumentDetailsPaymentStatus } from '../../../../../slices/marketplace/timeline/models/enums/document-details-payment-status';
import { TimelineActionType } from '../../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEvent } from '../../../../../slices/marketplace/timeline/models/timeline-event.model';
import { EngagementDocumentType } from '../../../../../wallet/models/EngagementDocumentType';
import { NotificationFlag } from '../../../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../../../models/NotificationFlags/NotificationFlagsCategories';

interface BuyersEngagementDocumentWithSelect
    extends MarketplaceOpportunityDocument {
    isSelected: boolean;
    isDisabled: boolean;
}

interface BuyersEngagementDocumentsSectionProps {
    engagementDocumentsSection: DocumentSection;
    isSelectModeEnabled: boolean;
    selectedDocumentsIds: string[];
    onDocumentSelected: (documentId: string) => void;
    downloadableDocDetailsFilterCondition: (
        t: MarketplaceOpportunityDocument
    ) => boolean;
    isGettingForSign: boolean;
    setIsGettingForSign: (isGettingForSign: boolean) => void;
    preSelectedDocumentId: string;
    resetPreSelectedDocumentId(): void;
    onDocumentDownload(documentDetailsId: string): void;
    setDownloadableDocumentsIds: (ids: string[]) => void;
    reloadDocumentsToggle: boolean;
    reloadDocuments: () => void;
    isReadOnly: boolean;
    notificationFlagPrefix: string;
}

const BuyersEngagementDocumentsSection = (
    props: BuyersEngagementDocumentsSectionProps
) => {
    const dispatch = useDispatch() as any;
    const axios = useStateSelector((t) => t.core.axios);
    const engagementDocuments = useStateSelector(
        (t) =>
            t.opportunities.selectedOpportuity.documentTables?.[
                props.engagementDocumentsSection.id
            ]
    );
    const opportunity = useStateSelector(
        (t) => t.opportunities.selectedOpportuity
    );
    const targetBuyer = useStateSelector(
        (state) => state.buyersState.targetBuyer
    );
    const marketplacePricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    const [opportunityFteDvmsCount, setOpportunityFteDvmsCount] = useState(0);

    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [documents, setDocuments] = useState<
        BuyersEngagementDocumentWithSelect[]
    >([]);
    const [highlightedDocumentId, setHighlightedDocumentId] =
        useState<string>();
    const [linkedProjectToView, setLinkedProjectToView] =
        useState<LinkedDataRequestProject>();
    const [isProjectItemViewPopupVisible, setIsProjectItemViewPopupVisible] =
        useState(false);
    const [questionnaireDocument, setQuestionnaireDocument] =
        useState<EngagementDocument>();
    const [
        isDocumentAccessFeeCheckoutPopupVisible,
        setIsDocumentAccessFeeCheckoutPopupVisible,
    ] = useState(false);
    const [paymentPricingDetails, setPaymentPricingDetails] =
        useState<EngagementPaymentPricingDetails>();
    const [documentToPayEventId, setDocumentToPayEventId] = useState<
        string | null
    >(null);

    useEffect(() => {
        if (props.preSelectedDocumentId) {
            setHighlightedDocumentId(props.preSelectedDocumentId);
            resetHighlighting();
        }
    }, [props.preSelectedDocumentId]);

    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(5000);
    const [currentSort, setCurrentSort] = useState<TableFilter>(
        new TableFilter({ filters: [], logic: '' }, page, sizePerPage, {
            dir: 'asc',
            field: 'id',
        })
    );
    const [isBuyerProfilePopupVisible, setIsBuyerProfilePopupVisible] =
        useState(false);
    const [selectedEngagedBuyer, setSelectedEngagedBuyer] =
        useState<EngagementBuyer>(null);
    const [documentToSign, setDocumentToSign] = useState<number>();
    const [selectedTableRow, setSelectedTableRow] =
        useState<MarketplaceOpportunityDocument>(null);
    const [
        isLegalDocumentInfoConfirmationPopupVisible,
        setIsLegalDocumentInfoConfirmationPopupVisible,
    ] = useState(false);

    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'name',
            order: 'asc',
        },
    ];

    useEffect(() => {
        if (
            documents &&
            highlightedDocumentId &&
            documents.some((d) => d.id === highlightedDocumentId)
        ) {
            setIsExpanded(true);
        }
    }, [props.preSelectedDocumentId, documents]);

    useEffect(() => {
        if (props.isGettingForSign) {
            setDocuments(
                documents.map((doc) => ({ ...doc, isDisabled: true }))
            );
        } else {
            setDocuments(
                documents.map((doc) => ({ ...doc, isDisabled: false }))
            );
        }
    }, [props.isGettingForSign]);

    useEffect(() => {
        const documentsCopy = documents.map((doc) => {
            return {
                ...doc,
                isSelected: props.selectedDocumentsIds.includes(doc.id),
            };
        });
        setDocuments(documentsCopy);
    }, [props.selectedDocumentsIds]);

    useEffect(() => {
        fetchEngagementDocuments();

        const fteDvmsCount = opportunity?.practices
            .map((practice) => practice.fteDvms)
            .reduce(
                (accumulator, currentValue) => accumulator + currentValue,
                0
            );
        setOpportunityFteDvmsCount(fteDvmsCount);
    }, [opportunity?.opportunityId, props.reloadDocumentsToggle]);

    const resetHighlighting = () => {
        setTimeout(() => {
            props.resetPreSelectedDocumentId();
            setHighlightedDocumentId(null);
        }, 2500);
    };

    useEffect(() => {
        if (engagementDocuments) {
            props.setDownloadableDocumentsIds(
                engagementDocuments.documents
                    .filter((doc) =>
                        props.downloadableDocDetailsFilterCondition(doc)
                    )
                    .map((doc) => doc.id)
            );
            setDocuments(
                engagementDocuments.documents as Array<BuyersEngagementDocumentWithSelect>
            );
            setPage(engagementDocuments.pageNumber);
            setTotalSize(engagementDocuments.totalItems);
            setSizePerPage(engagementDocuments.pageSize);
        }
    }, [engagementDocuments]);

    const fetchEngagementDocuments = async (sort?: TableFilter) => {
        await dispatch(
            GetOpportunityDocuments({
                opportunityId: opportunity.opportunityId,
                sectionId: props.engagementDocumentsSection.id,
                filterParams: {
                    ...(sort ?? currentSort),
                    pageSize: sizePerPage,
                    pageNumber: page,
                },
            })
        );
    };

    const getDocumentEvent = (document: MarketplaceOpportunityDocument) => {
        if (
            document.contentType === DocumentDetailsContentType.FinancialModel
        ) {
            const financialModelSelectionEvent = opportunity.events
                .filter((f) => f.linkedAction)
                .find(
                    (f) =>
                        f.linkedAction.actionType ===
                        TimelineActionType.SellerBuyerSelectionDetailedFinancialModel
                );

            return financialModelSelectionEvent;
        }

        const buyerActionStatus = opportunity.buyer.actionState.find(
            (f) => f.documentDetailsId === document.id
        );
        const documentEvent = opportunity.events
            .filter((f) => f.linkedAction)
            .find(
                (f) =>
                    f.linkedAction?.id === buyerActionStatus?.actionId ||
                    f.linkedAction?.referenceItemId === document.id
            );

        return documentEvent;
    };

    const handleTableChange = (_type: any, newState: any) => {
        const { page, sizePerPage, sortField, sortOrder } = newState;
        const updatedPostObj = new TimelineDocumentsTableFilter(
            { filters: [], logic: '' },
            page,
            sizePerPage,
            { dir: sortOrder, field: sortField }
        );

        const arrayOfObj = Object.entries(newState.filters).map((e) => ({
            [e[0]]: e[1],
        }));

        if (arrayOfObj.length > 0) {
            arrayOfObj.forEach((element) => {
                let key = Object.keys(element);

                let values = newState.filters[key.toString()];

                if (
                    values.filterType != FILTER_TYPES.DATE ||
                    values.filterVal.date != null
                ) {
                    let obj = {
                        Field: key[0],
                        Operator:
                            values.filterType == FILTER_TYPES.DATE ||
                            values.filterType == FILTER_TYPES.SELECT
                                ? 'eq'
                                : 'contains',
                        Value:
                            values.filterType == FILTER_TYPES.DATE
                                ? moment(values.filterVal.date).format(
                                      'MM.DD.YYYY'
                                  )
                                : values.filterVal,
                    };

                    updatedPostObj.filter.filters.push(obj);
                }
            });
        }

        setCurrentSort(updatedPostObj);

        fetchEngagementDocuments(updatedPostObj);
    };

    const handlePaymentRequiredException = (
        eventId: string,
        actionType: TimelineActionType,
        documentId?: string
    ) => {
        dispatch(GetOpportunity(opportunity.opportunityId));
        setIsLegalDocumentInfoConfirmationPopupVisible(false);
        setDocumentToPayEventId(eventId);

        switch (actionType) {
            case TimelineActionType.SellerBuyerSelectionIdNda:
                const idNdaExecutionFeeDetails =
                    PricesCalculationHelper.getIdNDAExecutionFeeDetails(
                        marketplacePricing,
                        opportunityFteDvmsCount,
                        opportunity.levelOfService
                    );
                setPaymentPricingDetails({
                    pricePerFteDvm: idNdaExecutionFeeDetails.pricePerItem,
                    fteDvmsCount: idNdaExecutionFeeDetails.itemsCount,
                    creditablePercent:
                        idNdaExecutionFeeDetails.creditablePercent,
                    creditableAmount: idNdaExecutionFeeDetails.creditableAmount,
                    totalPrice: idNdaExecutionFeeDetails.totalPrice,
                    engagementId: opportunity.opportunityId,
                    eventId: eventId,
                    documentType: EngagementDocumentType.IdNda,
                    documentId: documentId,
                });
                setIsDocumentAccessFeeCheckoutPopupVisible(true);
                return;
            case TimelineActionType.SellerCustomSelectDataNdaTerms:
                const dataNdaExecutionFeeDetails =
                    PricesCalculationHelper.getDataNDAExecutionFeeDetails(
                        marketplacePricing,
                        opportunityFteDvmsCount,
                        opportunity.levelOfService
                    );
                setPaymentPricingDetails({
                    pricePerFteDvm: dataNdaExecutionFeeDetails.pricePerItem,
                    fteDvmsCount: dataNdaExecutionFeeDetails.itemsCount,
                    creditablePercent:
                        dataNdaExecutionFeeDetails.creditablePercent,
                    creditableAmount:
                        dataNdaExecutionFeeDetails.creditableAmount,
                    totalPrice: dataNdaExecutionFeeDetails.totalPrice,
                    engagementId: opportunity.opportunityId,
                    eventId: eventId,
                    documentType: EngagementDocumentType.DataNda,
                    documentId: documentId,
                });
                setIsDocumentAccessFeeCheckoutPopupVisible(true);
                return;
            default:
                return;
        }
    };

    const getDocumentToSign = (
        documentId: string,
        prefilledTags: PrefilledTags[],
        eventId: string,
        actionType: TimelineActionType
    ) => {
        setIsLoading(true);

        axios
            .post(
                `/api/marketplace/engagements/${opportunity.opportunityId}/documents/${documentId}/sign`,
                prefilledTags
            )
            .then((response) => {
                if (response.status === 402) {
                    handlePaymentRequiredException(
                        eventId,
                        actionType,
                        documentId
                    );
                    return;
                }

                if (response.status !== 200) {
                    createNotification(
                        'An unexpected error occurred while getting Document',
                        'error'
                    );
                    return;
                }
                setDocumentToSign(response.data);
                setIsLegalDocumentInfoConfirmationPopupVisible(false);
                setIsLoading(false);
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred while getting Document',
                    'error'
                );
            })
            .finally(() => {
                dispatch(GetOpportunity(opportunity.opportunityId));
                fetchEngagementDocuments(currentSort);
                setIsLoading(false);
            });
    };

    const downloadFile = (documentDetailsId: string) => {
        axios
            .get(
                `/api/marketplace/${opportunity.opportunityId}/MarketplaceOpportunityDocuments/${documentDetailsId}`
            )
            .then((response) => {
                if (response.status === 200) {
                    props.onDocumentDownload(documentDetailsId);
                    window.open(response.data, '_blank');
                }
            })
            .catch(() =>
                createNotification(
                    'Some unexpected error occured while downloading file.',
                    'error'
                )
            );
    };
    useEffect(() => {
        const mappedDocuments = documents.map((doc) => {
            return {
                ...doc,
                isSelected: false,
            };
        });
        setDocuments(mappedDocuments);
    }, [props.isSelectModeEnabled]);

    const closeBuyerProfile = () => {
        setIsBuyerProfilePopupVisible(false);
        setSelectedEngagedBuyer(null);
    };

    const handldeSignedDocumentPreview = (documentDetailsId: string) => {
        axios
            .get(
                `/api/marketplace/engagements/${opportunity.opportunityId}/documentsDetails/${documentDetailsId}`
            )
            .then((response: any) => {
                if (response.status !== 200) {
                    createNotification(
                        response.response?.data?.detail ??
                            'An unexpected error occurred',
                        'error'
                    );
                    return;
                }

                window.open(response.data, '_blank');
            })
            .catch((response) => {
                createNotification(
                    response.response?.data?.detail ??
                        'An unexpected error occurred',
                    'error'
                );
            });
    };

    const startSigningDocument = (
        documentDetails: MarketplaceOpportunityDocument
    ) => {
        if (isLoading) {
            return;
        }

        if (documentDetails.engagementBuyerId === targetBuyer.id) {
            var documentEvent = getDocumentEvent(documentDetails);
            getDocumentToSign(
                documentDetails.id,
                [],
                documentEvent.id,
                documentEvent.linkedAction.actionType
            );
        } else {
            setIsLegalDocumentInfoConfirmationPopupVisible(true);
        }
    };

    const handleDocumentPayClick = (
        event: TimelineEvent,
        document: BuyersEngagementDocumentWithSelect
    ) => {
        const eventId = event?.id;
        setDocumentToPayEventId(eventId);
        if (
            document.contentType === DocumentDetailsContentType.FinancialModel
        ) {
            const financialModelAccessFeeDetails =
                PricesCalculationHelper.getFinancialModelFeeDetails(
                    marketplacePricing,
                    opportunityFteDvmsCount,
                    opportunity.levelOfService
                );
            setPaymentPricingDetails({
                pricePerFteDvm: financialModelAccessFeeDetails.pricePerItem,
                fteDvmsCount: financialModelAccessFeeDetails.itemsCount,
                creditablePercent:
                    financialModelAccessFeeDetails.creditablePercent,
                creditableAmount:
                    financialModelAccessFeeDetails.creditableAmount,
                totalPrice: financialModelAccessFeeDetails.totalPrice,
                engagementId: opportunity.opportunityId,
                eventId: eventId,
                documentType: EngagementDocumentType.FinancialModel,
                documentId: document.id,
            });
            setIsDocumentAccessFeeCheckoutPopupVisible(true);
            return;
        }
    };

    const handleDocmentNameClick = (
        document: MarketplaceOpportunityDocument
    ) => {
        if (
            document.type === ActionDocumentType.DocumentWithFeedback &&
            document.contentType === DocumentDetailsContentType.PipOrPiq &&
            document.source === EngagementDocumentSource.Questionnaire
        ) {
            if (
                opportunity.levelOfService === LevelOfService.DiscountBrokerage
            ) {
                const project =
                    opportunity.engagementData.dataRequestProjects.find(
                        (p) => p.id === document?.linkedProjectId
                    );
                setLinkedProjectToView(project);
                setIsProjectItemViewPopupVisible(true);

                setQuestionnaireDocument(
                    document as unknown as EngagementDocument
                );
            }
        } else if (document.type === ActionDocumentType.ForSign) {
            if (
                document.status === EngagementDocumentStatus.Completed ||
                document.counterParty === TimelineParty.Seller
            ) {
                return handldeSignedDocumentPreview(document.id);
            } else {
                const documentEvent = getDocumentEvent(document);
                const isEventActive =
                    documentEvent?.status === TimelineEventStatus.InProgress ||
                    documentEvent?.status === TimelineEventStatus.Late;
                if (isEventActive && !props.isReadOnly) {
                    setSelectedTableRow(document);
                    startSigningDocument(document);
                }
            }
        } else {
            return downloadFile(document.id);
        }
    };

    const getDocumentColumns = () =>
        [
            props.isSelectModeEnabled && {
                dataField: 'id',
                text: 'Select',
                headerClasses: 'document-select-column-header',
                formatter: (
                    cellContent: string,
                    row: BuyersEngagementDocumentWithSelect
                ) => (
                    <input
                        key={cellContent}
                        type="checkbox"
                        checked={
                            row.isSelected ??
                            props.selectedDocumentsIds.includes(cellContent)
                        }
                        disabled={
                            !props.downloadableDocDetailsFilterCondition(row) ||
                            row.paymentStatus ===
                                DocumentDetailsPaymentStatus.Unpaid ||
                            props.isReadOnly
                        }
                        onChange={() => {
                            props.onDocumentSelected(cellContent);
                        }}
                    />
                ),
            },
            {
                dataField: 'name',
                text: 'Name',
                headerClasses: 'document-name-column-header',
                formatter: (
                    cellContent: string,
                    row: MarketplaceOpportunityDocument
                ) => {
                    if (!cellContent) {
                        return 'NA';
                    }

                    if (
                        row.paymentStatus ===
                            DocumentDetailsPaymentStatus.Unpaid &&
                        opportunity.levelOfService !==
                            LevelOfService.StrategicFinancialAdvisoryService
                    ) {
                        return (
                            <div className='doc-name-no-link'>
                                {cellContent}

                                <NotificationFlag
                                    eventCode={`${props.notificationFlagPrefix}.${props.engagementDocumentsSection.id}.${row.id}`}
                                    includePrefix={true}
                                />
                            </div>
                        );
                    }

                    return (
                        <a
                            className="doc-name"
                            onClick={() => {
                                handleDocmentNameClick(row);
                            }}>
                            {cellContent}

                            <NotificationFlag
                                eventCode={`${props.notificationFlagPrefix}.${props.engagementDocumentsSection.id}.${row.id}`}
                                includePrefix={true}
                            />
                        </a>
                    );
                },
            },
            {
                dataField: 'dateCreated',
                text: 'Date Posted',
                headerClasses: 'date-posted-column-header',
                formatter: (cellContent: Date) => (
                    <div>
                        {cellContent
                            ? moment(cellContent).format('M/D/YYYY')
                            : 'NA'}
                    </div>
                ),
            },
            {
                dataField: 'deadline',
                text: 'Deadline',
                headerClasses: 'deadline-column-header',
                formatter: (cellContent: Date) => (
                    <div>
                        {cellContent
                            ? moment(cellContent).format('M/D/YYYY')
                            : 'NA'}
                    </div>
                ),
            },
            {
                dataField: 'status',
                text: 'Actions',
                headerClasses: 'status-column-header',
                formatter: (
                    _: any,
                    row: BuyersEngagementDocumentWithSelect
                ) => {
                    if (
                        props.isReadOnly ||
                        opportunity.levelOfService ===
                            LevelOfService.StrategicFinancialAdvisoryService
                    ) {
                        return <></>;
                    }

                    const isDocumentToSign =
                        row.type === ActionDocumentType.ForSign &&
                        row.status !== EngagementDocumentStatus.Completed &&
                        row.counterParty !== TimelineParty.Seller;
                    const documentEvent = getDocumentEvent(row);
                    const isEventActive =
                        documentEvent?.status ===
                            TimelineEventStatus.InProgress ||
                        documentEvent?.status === TimelineEventStatus.Late;

                    return (
                        <>
                            {row.paymentStatus ===
                            DocumentDetailsPaymentStatus.Unpaid ? (
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        handleDocumentPayClick(
                                            documentEvent,
                                            row
                                        );
                                    }}>
                                    {'Pay Access Fee'}
                                </button>
                            ) : (
                                <></>
                            )}

                            {isDocumentToSign && isEventActive ? (
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        setSelectedTableRow(row);
                                        startSigningDocument(row);
                                    }}>
                                    {'Confirm Information & Sign Document'}
                                </button>
                            ) : (
                                <></>
                            )}
                        </>
                    );
                },
            },
        ].filter(Boolean);

    const handleSuccessfulPayment = () => {
        props.reloadDocuments();
        setIsDocumentAccessFeeCheckoutPopupVisible(false);
        const document = documents.find(
            (d) => d.id === paymentPricingDetails.documentId
        );
        handleDocmentNameClick(document);
    };

    return (
        <div className="document-section">
            <CollapsibleComponent
                isExpanded={isExpanded}
                onToggle={(isExpanded) => {
                    setIsExpanded(isExpanded);
                }}
                header={props.engagementDocumentsSection.name}
                notificationFlag={`${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${opportunity.opportunityId}.${NotificationFlagsCategories.EngagementDocumentsCode}.${props.engagementDocumentsSection.id}`}>
                <div
                    role="tabpanel"
                    id={`panel-${props.engagementDocumentsSection.id}`}
                    aria-labelledby={`tab-${props.engagementDocumentsSection.id}`}
                    aria-hidden="false">
                    <div className="card-body">
                        <BootstrapTable
                            striped
                            bordered
                            bootstrap4
                            remote
                            keyField="id"
                            data={documents}
                            columns={getDocumentColumns()}
                            defaultSorted={defaultSorted}
                            filter={filterFactory()}
                            headerClasses="header-vetrical-align-top"
                            pagination={undefined}
                            onTableChange={handleTableChange}
                            noDataIndication="No Documents to display"
                            rowStyle={{ height: '44px' }}
                            rowClasses={(row, index) => {
                                if (highlightedDocumentId === row.id) {
                                    return index % 2
                                        ? 'highlighted'
                                        : 'highlighted-dark';
                                }

                                return '';
                            }}
                        />
                    </div>
                </div>
            </CollapsibleComponent>

            {documentToSign ? (
                <DocumentSignModal
                    documentId={documentToSign}
                    isVisible={!!documentToSign}
                    onClose={() => setDocumentToSign(undefined)}
                    onSign={() => {
                        setTimeout(() => {
                            setDocumentToSign(undefined);
                            createNotification(
                                'Document signed successfully',
                                'success'
                            );
                        }, 2000);
                        fetchEngagementDocuments(currentSort).then(() => {
                            dispatch(GetOpportunity(opportunity.opportunityId));
                        });
                    }}
                />
            ) : (
                []
            )}
            {isBuyerProfilePopupVisible ? (
                <BuyerProfilePreviewPopup
                    buyerId={selectedEngagedBuyer.buyerId}
                    close={closeBuyerProfile}
                />
            ) : (
                <></>
            )}

            {isLegalDocumentInfoConfirmationPopupVisible ? (
                <LegalDocumentInformationPopup
                    opportunityId={opportunity.opportunityId}
                    isLoading={isLoading}
                    onSubmit={(prefilledTags) => {
                        const documentEvent =
                            getDocumentEvent(selectedTableRow);

                        getDocumentToSign(
                            selectedTableRow.id,
                            prefilledTags,
                            documentEvent.id,
                            documentEvent.linkedAction.actionType
                        );
                    }}
                    onClose={() =>
                        setIsLegalDocumentInfoConfirmationPopupVisible(false)
                    }
                    documentType={selectedTableRow.type}
                />
            ) : (
                <></>
            )}
            {isProjectItemViewPopupVisible && linkedProjectToView ? (
                <ProjectItemViewPopup
                    isVisible={isProjectItemViewPopupVisible}
                    setIsVisible={(visible: boolean) => {
                        setIsProjectItemViewPopupVisible(visible);
                        if (!visible) {
                            setLinkedProjectToView(undefined);
                        }
                    }}
                    linkedProject={linkedProjectToView}
                    engagementStartDate={opportunity.listingDate}
                    questionnaireDocument={questionnaireDocument}
                />
            ) : (
                <></>
            )}

            {isDocumentAccessFeeCheckoutPopupVisible && (
                <EngagementDocumentAccessPaymentComponent
                    isVisible={isDocumentAccessFeeCheckoutPopupVisible}
                    setIsVisible={setIsDocumentAccessFeeCheckoutPopupVisible}
                    paymentSuccess={() => handleSuccessfulPayment()}
                    engagementId={opportunity.opportunityId}
                    eventId={documentToPayEventId}
                    fteDvmsCount={paymentPricingDetails.fteDvmsCount}
                    pricePerFteDmv={paymentPricingDetails.pricePerFteDvm}
                    totalPrice={paymentPricingDetails.totalPrice}
                    creditableAmount={paymentPricingDetails.creditableAmount}
                    documentType={paymentPricingDetails.documentType}
                    documentId={paymentPricingDetails.documentId}
                />
            )}
        </div>
    );
};

export default BuyersEngagementDocumentsSection;
